import { resetSession } from '@/utils/session';
import Axios, { AxiosError, AxiosRequestConfig } from 'axios';

export const axios = Axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
});

// Stamp every request
axios.interceptors.request.use((request) => {
  request.headers!['X-Client-Type'] = 'web';
  request.headers!['X-Tenant'] = window.location.hostname.split('.')[0];
  return request;
});

let isRefreshing = false;
let refreshSubscribers: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

function onRefreshed() {
  refreshSubscribers.forEach(({ resolve }) => resolve(undefined));
  refreshSubscribers = [];
}

function onRefreshFailed(err: unknown) {
  refreshSubscribers.forEach(({ reject }) => reject(err));
  refreshSubscribers = [];
}

function addRefreshSubscriber() {
  return new Promise((resolve, reject) => {
    refreshSubscribers.push({ resolve, reject });
  });
}

axios.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const { status } = error.response || {};
    const originalReq = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (
      status === 401 &&
      !originalReq._retry &&
      !originalReq.url?.includes('/refresh')
    ) {
      originalReq._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await axios.post('/api/auth/refresh');
          onRefreshed();
        } catch (err) {
          onRefreshFailed(err);
          await resetSession();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      } else {
        try {
          await addRefreshSubscriber();
        } catch (err) {
          return Promise.reject(err);
        }
      }

      return axios(originalReq);
    }

    if (status === 401 && originalReq._retry) {
      await resetSession();
    }

    return Promise.reject(error);
  }
);
